// A latitude e a longitude do IfcSite, escritas a partir da
// georreferenciação (IfcSite.RefLatitude / RefLongitude).
//
// No Cha139 (15/10/2026) estavam erradas em três dos cinco modelos: o
// EST no Massachusetts e o MAC e o EEL 8 km ao lado, os valores por
// omissão do Revit ("Location" nunca definida). Há programas que as
// usam quando não há IfcMapConversion (e a plataforma também, como
// último recurso: `lerLocalizacaoDoSitio`), por isso, ao gravar a
// georreferenciação, passam a ser as da origem do IfcSite no mapa.
import type { GeoPlacement } from '@viewer/lib/geoTransform'
import { findEntityById, findEntityByType, splitTopLevelArgs } from '@viewer/lib/stepText'

/** Um ângulo em graus decimais como IfcCompoundPlaneAngleMeasure:
 *  (graus, minutos, segundos, milionésimos de segundo), todos com o
 *  sinal do ângulo. */
export function anguloCompostoStep(graus: number): string {
  if (!Number.isFinite(graus)) throw new Error('Ângulo inválido.')
  const sinal = graus < 0 ? -1 : 1
  // Em milionésimos de segundo inteiros, para o arredondamento não dar
  // 60 segundos.
  let micro = Math.round(Math.abs(graus) * 3600 * 1e6)
  const g = Math.floor(micro / (3600 * 1e6))
  micro -= g * 3600 * 1e6
  const m = Math.floor(micro / (60 * 1e6))
  micro -= m * 60 * 1e6
  const s = Math.floor(micro / 1e6)
  micro -= s * 1e6
  return `(${[g, m, s, micro].map((x) => (x === 0 ? 0 : sinal * x)).join(',')})`
}

function ref(arg: string | undefined): number | null {
  const m = (arg ?? '').trim().match(/^#(\d+)$/)
  return m ? Number(m[1]) : null
}

/** A origem do primeiro IfcSite nas coordenadas do ficheiro (unidade do
 *  projecto): a localização da sua colocação. `relativa` quando a
 *  colocação é relativa a outra (raro num IfcSite): aí não se usa. */
export function origemDoSitio(
  texto: string,
): { id: number; x: number; y: number; relativa: boolean } | null {
  const sitio = findEntityByType(texto, 'IFCSITE')
  if (!sitio) return null
  const idSitio = Number(texto.slice(sitio.start + 1).match(/^(\d+)/)?.[1] ?? Number.NaN)
  const args = splitTopLevelArgs(sitio.args)
  const lp = ref(args[5])
  const local = lp !== null ? findEntityById(texto, lp) : null
  if (!local || local.type !== 'IFCLOCALPLACEMENT') return null
  const [relTo, rel] = splitTopLevelArgs(local.args)
  const eixos = ref(rel) !== null ? findEntityById(texto, ref(rel)!) : null
  if (!eixos) return null
  const ponto = findEntityById(texto, ref(splitTopLevelArgs(eixos.args)[0]) ?? -1)
  if (!ponto || ponto.type !== 'IFCCARTESIANPOINT') return null
  const coords = ponto.args.replace(/[()\s]/g, '').split(',').map(Number)
  if (coords.length < 2 || !coords.slice(0, 2).every(Number.isFinite)) return null
  return { id: idSitio, x: coords[0], y: coords[1], relativa: relTo.trim() !== '$' }
}

/** Um ponto do ficheiro (unidade do projecto) no mapa, em metros, pela
 *  georreferenciação: E/N + rotação e escala do placement. */
export function pontoNoMapa(
  x: number,
  y: number,
  placement: GeoPlacement,
  metrosPorUnidade: number,
): [number, number] {
  const th = (placement.rotationDeg * Math.PI) / 180
  const s = (placement.scale || 1) * metrosPorUnidade
  return [
    placement.eastings + s * (x * Math.cos(th) - y * Math.sin(th)),
    placement.northings + s * (x * Math.sin(th) + y * Math.cos(th)),
  ]
}

/** Escreve RefLatitude e RefLongitude (10.º e 11.º atributos) em todos
 *  os IfcSite. Devolve o texto novo e quantos mudaram. */
export function escreverLatLonNoSitio(
  texto: string,
  lat: number,
  lon: number,
): { texto: string; alterados: number } {
  let alterados = 0
  const novo = texto.replace(/(#\d+\s*=\s*IFCSITE\s*\()([\s\S]*?)(\)\s*;)/g, (todo, a: string, corpo: string, c: string) => {
    const partes = splitTopLevelArgs(corpo)
    if (partes.length < 11) return todo
    const antes = `${partes[9]}|${partes[10]}`
    partes[9] = anguloCompostoStep(lat)
    partes[10] = anguloCompostoStep(lon)
    if (`${partes[9]}|${partes[10]}` !== antes) alterados++
    return a + partes.join(',') + c
  })
  return { texto: novo, alterados }
}
